import type { IResource, IResourceRecruitmentDate } from '../../interface/src/index.ts'
import { datesAreOnSameDay } from '../../common/src/index.ts'

const test = <T extends { prop: number }>(param: T) => {
  return param
}
test({ hallo: 1, prop: 1 })

type currentJobShouldContain = {
  jobs: Array<{ _id: string; startDate: string | Date; endDate?: string | Date; isAlumni: boolean }>
}

/**
 * Get the job of the moment
 * @param jobs
 * @return ICurrentJob | null
 */
export function getCurrentJob({ jobs }: any): any {
  // The job covering today, its last day included
  const getJobToday = getJobOnDay({ jobs }, new Date())

  let thisJobs = null

  if (!jobs || jobs.length === 0)
    return null

  if (getJobToday !== null)
    return getJobToday

  // Sort a copy: the caller's jobs (often reactive) must keep their order
  if (jobs && jobs.length > 0)
    thisJobs = [...jobs]

  if (thisJobs === null)
    return null

  thisJobs.sort((obj: any) => {
    if (!obj.endDate)
      return -1
    return (obj.endDate instanceof Date ? obj.endDate : new Date(obj.endDate)).getTime()
  })
  thisJobs.reverse()

  for (let k = 0; k < thisJobs.length; k++) {
    if (thisJobs[k].endDate === null || thisJobs[k].endDate === undefined)
      return thisJobs[k]
  }
  return thisJobs[0]
}

/**
 * Get the job to a given period
 * @param jobs
 * @param date
 * @return ICurrentJob | null
 */
export function getJobInPeriod<T>(
  { jobs }: any,
  date: Date,
) {
  const earliestJob = getEarliestJob({ jobs })

  for (let k = 0; k < jobs.length; k++) {
    const userStartDate = new Date(jobs[k].startDate)
    userStartDate.setHours(0, 0, 0, 0)

    if (earliestJob?._id == jobs[k]._id)
      userStartDate.setHours(0, 0, 0, 1)

    if (datesAreOnSameDay(userStartDate, date) || userStartDate < date && !jobs[k].endDate)
      return jobs[k]
    if (jobs[k].endDate && userStartDate <= date && new Date(jobs[k].endDate as string) >= date) {
      if (jobs[k].isAlumni)
        return null

      return jobs[k]
    }
    if (!jobs[k].startDate)
      return jobs[k]
  }
  return null
};

/**
 * Get the job covering a calendar day: from its start date to its end date, both days included.
 * Alumni periods record time away between two contracts and never count.
 * The job dates must be business dates converted with newDateFromUTC.
 * @param jobs
 * @param date
 * @return the job, or null outside of any contract
 */
export function getJobOnDay({ jobs }: any, date: Date) {
  const day = new Date(date)
  day.setHours(0, 0, 0, 0)

  return (jobs || []).find((job: any) => {
    if (job.isAlumni)
      return false

    const startDay = job.startDate ? new Date(job.startDate) : null
    startDay?.setHours(0, 0, 0, 0)
    const endDay = job.endDate ? new Date(job.endDate) : null
    endDay?.setHours(0, 0, 0, 0)

    return (!startDay || startDay <= day) && (!endDay || day <= endDay)
  }) ?? null
}

/**
 * Get the first job
 * @param jobs
 * @return IResourceJob | null
 */
export function getEarliestJob({ jobs }: { jobs: Array<{ _id: string; startDate: string | Date }> }) {
  let thisJob = null
  if (!jobs?.length)
    return null
  let thisDate = new Date(jobs[0].startDate)
  thisJob = jobs[0]
  for (let i = 0; i < jobs.length; i++) {
    if (new Date(jobs[i].startDate).getTime() < thisDate.getTime()) {
      thisDate = new Date(jobs[i].startDate)
      thisJob = jobs[i]
    }
  }
  return thisJob
}

/**
 * Get the date of the recruitment
 * @param resource
 * @return Date
 */
export function getRecruitmentDate(resource: Pick<IResourceRecruitmentDate, 'recruitmentDate' | 'jobs'>): Date {
  const thisJob = resource.jobs.find((el) => {
    return el.isStartDate
  })
  if (thisJob)
    return new Date(thisJob.startDate)

  return new Date(resource.recruitmentDate)
}

// new interface interval where the start and end date are optional and year is optional too
export type IInterval = {
  startDate: Date
  endDate: Date
}

export function getResourceLOA(resource: Pick<IResource, 'jobs' | 'recruitmentDate'>): IInterval[] {
  const startDate = getRecruitmentDate(resource)
  const jobs = resource.jobs.filter(job => (job.isAlumni === false || job.startDate < startDate))
  jobs.sort((a, b) => {
    return new Date(a.startDate).getTime() - new Date(b.startDate).getTime()
  })

  const loaList: IInterval[] = []
  jobs.forEach((job) => {
    if (job.startDateLOA && job.endDateLOA) {
      const loa = {
        startDate: new Date(job.startDateLOA),
        endDate: new Date(job.endDateLOA),
      }
      loaList.push(loa)
    }
  })

  return loaList
}

/**
 * Calculates a resource's total seniority in months. It correctly handles gaps and
 * overlaps by merging job intervals before calculating the total duration.
 *
 * @param {object} resource - The resource object with a 'jobs' array.
 * @param {Date | string | null} departureDate - The resource's departure date, if any.
 * @returns {number} The total seniority in rounded months.
 */
export const getSeniority = (resource:any, departureDate = null) => {
  if (!resource || !resource.jobs || resource.jobs.length === 0) {
    return 0;
  }
  
  const jobs = resource.jobs.filter((job:any) => !job.isAlumni)

  return jobs.reduce((acc:any, job:any) => {
    const tig = getTIG(job, departureDate)
    return acc + tig
  }, 0)

};

/**
 * Calculates the Time in Grade (TIG) for a SINGLE job in days.
 * It accounts for the job's specific start date, end date, and any Leave of Absence (LOA).
 *
 * @param {object} job - The job object with startDate, endDate, startDateLOA, endDateLOA.
 * @param {Date | string | null} customEndDate - An optional end date to cap the calculation (e.g., today or a departure date).
 * @returns {number} The duration in rounded days. Returns 0 if the job is invalid.
 */
export const getTIG = (job:any, customEndDate = null) => {
  if (!job || !job.startDate) {
    return 0; // A job must exist and have a start date.
  }

  // --- Date Preparation ---
  const startDate = new Date(job.startDate);

  // Determine the effective end date for calculation. It's the earliest of:
  // 1. The job's own end date.
  // 2. A custom end date passed in (like a global departure date).
  // 3. Today's date (if the job is ongoing and no custom end date is provided).
  const endDateCandidates = [customEndDate ? new Date(customEndDate) : new Date()];
  if (job.endDate) {
    endDateCandidates.push(new Date(job.endDate));
  }

  const effectiveEndDate = new Date(Math.min(...endDateCandidates.map(date => date.getTime())));

  if (startDate >= effectiveEndDate) {
    return 0; // Job duration is zero if it starts on or after its end date.
  }

  // --- LOA (Leave of Absence) Calculation ---
  let loaMs = 0;
  if (job.startDateLOA) {
    const startDateLOA = new Date(job.startDateLOA);
    // LOA period is also capped by the job's effective end date.
    const endDateLOA = job.endDateLOA ? new Date(job.endDateLOA) : effectiveEndDate;
    const effectiveEndDateLOA = new Date(Math.min(endDateLOA.getTime(), effectiveEndDate.getTime()));

    if (startDateLOA < effectiveEndDateLOA) {
      loaMs = effectiveEndDateLOA.getTime() - startDateLOA.getTime();
    }
  }

  // --- Final Calculation ---
  const grossDurationMs = effectiveEndDate.getTime() - startDate.getTime();
  const netDurationMs = Math.max(0, grossDurationMs - loaMs);

  // Convert net milliseconds to days
  const millisecondsInDay = 1000 * 60 * 60 * 24;
  const days = netDurationMs / millisecondsInDay;

  return Math.round(days);
};

/**
 * Finds the current job and all immediately preceding jobs with the same positionId.
 * It iterates backwards from the end of the jobs array and stops as soon as a 
 * job with a different positionId is found.
 *
 * @param {object} resource - The resource object containing a `jobs` array and a `currentJob`.
 * @returns {Array<object>} An array of job objects that are part of the current consecutive grade.
 */
export const getConsecutiveJobsForCurrentGrade = (resource: IResource) => {
  // 1. Handle edge cases where data might be missing or invalid.


  if (!resource?.currentJob?.positionId || !Array.isArray(resource.jobs) || resource.jobs.length === 0) {
    return [];
  }

  const { currentJob, jobs } = resource;
  const currentPositionId = currentJob.positionId;

  const sameJobsAsCurrentJob = jobs.filter(job => job.positionId === currentPositionId); // Simplified logic using filter for all matching jobs

  // const sameJobsAsCurrentJob = [];
  // // 2. Iterate backwards through the jobs history (which must be in chronological order).
  // for (let i = jobs.length - 1; i >= 0; i--) {
  //   const job = jobs[i];
  //   // 3. If the job's positionId matches the current one, add it to our list.
  //   if (job.positionId === currentPositionId) {
  //     // unshift() adds the item to the beginning of the array,
  //     // so the final list will be in the correct chronological order.


  //     sameJobsAsCurrentJob.unshift(job);
  //   } else {
  //     // 4. If the positionId does NOT match, the consecutive streak is broken, so we stop looking.
  //     break;
  //   }
  // }

  return sameJobsAsCurrentJob;
};
